import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  User,
  Calendar,
  Search,
  Trash2,
  Eye,
  ThumbsUp,
  ThumbsDown,
  AlertCircle,
} from "lucide-react";
import { conversationService } from "../../services/conversationService";
import { useAuth } from "../../contexts/AuthContext";
import type {
  Conversation,
  ConversationListResponse,
  ConversationListParams,
} from "../../types/conversation";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

function Pagination({
  currentPage,
  totalPages,
  onPageChange,
}: PaginationProps) {
  return (
    <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
      <div className="flex justify-between flex-1 sm:hidden">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 0}
          className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages - 1}
          className="relative ml-3 inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            Page <span className="font-medium">{currentPage + 1}</span> of{" "}
            <span className="font-medium">{totalPages}</span>
          </p>
        </div>
        <div>
          <nav
            className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
            aria-label="Pagination"
          >
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 0}
              className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" aria-hidden="true" />
            </button>
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages - 1}
              className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-r-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5" aria-hidden="true" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
}

function ConversationRow({
  conversation,
  onView,
  onDelete,
}: {
  conversation: Conversation;
  onView: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  const formatDate = (dateString: string) => {
    // Ensure the date string is treated as UTC if it doesn't have timezone info
    const utcDateString = dateString.endsWith("Z")
      ? dateString
      : dateString + "Z";
    const date = new Date(utcDateString);
    return date.toLocaleString();
  };

  const formatUserId = (userId?: string) => {
    if (!userId) return "Anonymous";
    return userId.length > 20 ? `${userId.substring(0, 20)}...` : userId;
  };

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
        <div className="flex items-center">
          <MessageSquare className="w-4 h-4 mr-2 text-gray-400" />
          {conversation.id.substring(0, 8)}...
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        <div className="flex items-center">
          <User className="w-4 h-4 mr-2 text-gray-400" />
          {formatUserId(conversation.user_id)}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        <div className="flex items-center">
          <Calendar className="w-4 h-4 mr-2 text-gray-400" />
          {formatDate(conversation.started_at)}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {conversation.ended_at ? formatDate(conversation.ended_at) : "Active"}
      </td>
      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs">
        <div
          className="truncate"
          title={conversation.first_message_snippet || "No message"}
        >
          {conversation.first_message_snippet || "No message"}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {conversation.message_count || 0} messages
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <ThumbsUp className="w-3 h-3 text-green-500" />
            <span className="text-green-600 text-xs font-medium">
              {conversation.feedback_stats?.positive_count || 0}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <ThumbsDown className="w-3 h-3 text-red-500" />
            <span className="text-red-600 text-xs font-medium">
              {conversation.feedback_stats?.negative_count || 0}
            </span>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex justify-end space-x-2">
          <button
            onClick={() => onView(conversation.id)}
            className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50"
            title="View conversation"
          >
            <Eye className="w-4 h-4" />
          </button>
          {/* <button
            onClick={() => onDelete(conversation.id)}
            className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
            title="Delete conversation"
          >
            <Trash2 className="w-4 h-4" />
          </button> */}
        </div>
      </td>
    </tr>
  );
}

export function ConversationsPage(): JSX.Element {
  const navigate = useNavigate();
  const { clerkUser } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [userIdFilter, setUserIdFilter] = useState("");
  const [filterUnanswered, setFilterUnanswered] = useState(false);
  const pageSize = 20;

  const fetchConversations = async (params: ConversationListParams = {}) => {
    try {
      setLoading(true);
      setError(null);

      const response = await conversationService.getConversations({
        page: currentPage,
        size: pageSize,
        user_id: userIdFilter || undefined,
        has_unanswered: filterUnanswered ? true : undefined,
        ...params,
      });

      setConversations(response.conversations);
      setTotal(response.total);
      setTotalPages(Math.ceil(response.total / pageSize));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load conversations"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConversations({ page: currentPage });
  }, [currentPage, filterUnanswered]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleSearch = () => {
    setCurrentPage(0);
    fetchConversations({ page: 0 });
  };

  const handleViewConversation = (conversationId: string) => {
    navigate(`/admin/conversations/${conversationId}`);
  };

  const handleDeleteConversation = async (conversationId: string) => {
    if (
      !confirm(
        "Are you sure you want to delete this conversation? This action cannot be undone."
      )
    ) {
      return;
    }

    if (!clerkUser) {
      alert("User not authenticated");
      return;
    }

    try {
      await conversationService.deleteConversation(conversationId);
      // Refresh the current page
      fetchConversations({ page: currentPage });
    } catch (err) {
      alert(
        err instanceof Error ? err.message : "Failed to delete conversation"
      );
    }
  };

  if (loading && conversations.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading conversations...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">Conversations</h1>
          <p className="mt-2 text-sm text-gray-700">
            A list of all conversations in the system ordered by creation date.
          </p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label
                htmlFor="user-id"
                className="block text-sm font-medium text-gray-700"
              >
                Filter by User ID
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <input
                  type="text"
                  id="user-id"
                  value={userIdFilter}
                  onChange={(e) => setUserIdFilter(e.target.value)}
                  className="block w-full pr-10 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter user ID to filter..."
                />
              </div>
            </div>
            <div className="flex items-end gap-4">
              <button
                onClick={() => {
                  setFilterUnanswered(!filterUnanswered);
                  setCurrentPage(0);
                }}
                className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 ${
                  filterUnanswered
                    ? "bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                <AlertCircle className={`w-4 h-4 mr-2 ${filterUnanswered ? "text-amber-600" : "text-gray-400"}`} />
                Questions sans réponse
              </button>
              <button
                onClick={handleSearch}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Search className="w-4 h-4 mr-2" />
                Search
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-700">{error}</div>
        </div>
      )}

      {/* Conversations Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Conversation ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Started At
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ended At
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                First Message
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Messages
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Feedback
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {conversations.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                  No conversations found
                </td>
              </tr>
            ) : (
              conversations.map((conversation) => (
                <ConversationRow
                  key={conversation.id}
                  conversation={conversation}
                  onView={handleViewConversation}
                  onDelete={handleDeleteConversation}
                />
              ))
            )}
          </tbody>
        </table>

        {conversations.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      {/* Summary */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="text-sm text-gray-700">
            Showing {conversations.length} of {total} conversations
            {userIdFilter && ` (filtered by user ID: ${userIdFilter})`}
            {filterUnanswered && " (showing only conversations with unanswered questions)"}
          </div>
        </div>
      </div>
    </div>
  );
}
